import type { Node } from '$domain/types';

export interface TreeNode {
  id: string;
  type: string;
  title: string;
  completed: boolean;
  depth: number;
  children: TreeNode[];
  totalLeaves: number;
  completedLeaves: number;
  progress: number;
  isLeaf: boolean;
}

export function buildNodeTree(items: Node[]): TreeNode[] {
  const childMap = new Map<string, Node[]>();
  const roots: Node[] = [];

  for (const item of items) {
    if (!item.parentId) {
      roots.push(item);
      continue;
    }
    if (!childMap.has(item.parentId)) childMap.set(item.parentId, []);
    childMap.get(item.parentId)!.push(item);
  }

  function buildNode(item: Node, depth: number): TreeNode {
    const children = childMap.get(item.id) ?? [];
    const childNodes = children
      .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title))
      .map((c) => buildNode(c, depth + 1));

    const isLeaf = childNodes.length === 0;
    let totalLeaves: number;
    let completedLeaves: number;

    if (isLeaf) {
      totalLeaves = 1;
      completedLeaves = item.completed ? 1 : 0;
    } else {
      totalLeaves = childNodes.reduce((s, n) => s + n.totalLeaves, 0);
      completedLeaves = childNodes.reduce((s, n) => s + n.completedLeaves, 0);
    }

    const progress = totalLeaves > 0 ? completedLeaves / totalLeaves : 0;

    return {
      id: item.id,
      type: item.type,
      title: item.title,
      completed: item.completed,
      depth,
      children: childNodes,
      totalLeaves,
      completedLeaves,
      progress,
      isLeaf
    };
  }

  return roots
    .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title))
    .map((r) => buildNode(r, 0));
}

export function overallProgress(tree: TreeNode[]): { completed: number; total: number; ratio: number } {
  let completed = 0;
  let total = 0;
  for (const node of tree) {
    completed += node.completedLeaves;
    total += node.totalLeaves;
  }
  return { completed, total, ratio: total > 0 ? completed / total : 0 };
}